import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES, BRAND } from "@/lib/constants";
import { fetchProjects } from "@/lib/api";

const SERVICES = [
  { no: "01", title: "Land & Housing", desc: "Residential, commercial, and mixed-use ground-up development across Alberta and the Caribbean." },
  { no: "02", title: "Property Flipping", desc: "Acquire, renovate, and resell distressed properties — target 20% to 40% ROI per project." },
  { no: "03", title: "International Investors", desc: "End-to-end representation for clients abroad — sourcing, legal, asset management." },
  { no: "04", title: "Hotels & Villas", desc: "Boutique villa and hotel development — currently building in Canelles, St. Lucia." },
];

const PROCESS = [
  { no: "01", title: "Consultation", desc: "Investment goals, risk profile, timelines." },
  { no: "02", title: "Sourcing", desc: "On- and off-market opportunities, vetted." },
  { no: "03", title: "Acquisition", desc: "Due diligence, structuring, closing." },
  { no: "04", title: "Development", desc: "Design, permits, construction oversight." },
  { no: "05", title: "Delivery", desc: "Lease-up, resale, or portfolio handover." },
];

const STATS = [
  { number: "12+", label: "Projects Delivered" },
  { number: "95%", label: "Client Satisfaction" },
  { number: "40%", label: "Avg. ROI" },
  { number: "24/7", label: "Investor Support" },
];

export default function Home({ onOpenConsult }) {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchProjects()
      .then((data) => {
        if (!cancelled) setProjects(data);
      })
      .catch(() => {
        /* fail silently — the featured projects section is a nice-to-have on Home */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const featured = useMemo(() => projects.slice(0, 3), [projects]);

  const marqueeItems = useMemo(
    () =>
      Array.from({ length: 2 }).flatMap((_, k) =>
        [
          "Land Development",
          "·",
          "Property Flipping",
          "·",
          "International Investors",
          "·",
          "Hotels & Villas",
          "·",
          "Project Management",
          "·",
        ].map((t, i) => ({ key: `${k}-${i}`, label: t, isDot: t === "·" }))
      ),
    []
  );

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="home-hero" className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Modern architecture" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[#0A1628]" />
        </div>
        <div className="grain absolute inset-0" />

        {/* side label */}
        <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 items-center gap-3">
          <div className="vertical-text">{BRAND.founded}</div>
          <div className="h-24 w-px bg-white/20" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-20 md:pb-28 pt-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl"
          >
            <div className="overline mb-8">Edmonton · Alberta · Canada</div>
            <h1 className="font-serif-display text-[12vw] sm:text-7xl md:text-8xl lg:text-[8.5rem] xl:text-[10rem] text-[#F3EFE4] leading-[0.86]">
              Building <span className="italic text-[#D4AF37]">today.</span>
              <br />
              Developing tomorrow.
            </h1>
            <div className="mt-10 grid md:grid-cols-2 gap-8 items-end">
              <p className="text-[#8FA3B8] text-base md:text-lg max-w-md leading-relaxed">
                A full-service real estate development and investment firm — from Edmonton land assemblies to luxury villa developments in the Caribbean. Currently active in Canelles, St. Lucia.
              </p>
              <div className="flex gap-4 flex-wrap md:justify-end">
                <button data-testid="hero-book-consult" onClick={onOpenConsult} className="btn-primary">
                  Book Consultation <span className="btn-arrow">→</span>
                </button>
                <Link to="/projects" data-testid="hero-view-projects" className="btn-secondary">
                  View Projects
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* bottom ticker */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0A1628]/70 backdrop-blur-sm py-4 overflow-hidden">
          <div className="marquee-track flex gap-16 whitespace-nowrap text-[#8FA3B8] text-xs tracking-[0.34em] uppercase">
            {marqueeItems.map((m) => (
              <span key={m.key} className={m.isDot ? "text-[#D4AF37]" : ""}>
                {m.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section data-testid="home-intro" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">A Word from U-NEXX</div>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-serif-display text-3xl md:text-5xl lg:text-6xl text-[#F3EFE4] leading-[1.05]">
                A full-service <span className="italic text-[#D4AF37]">land and housing developer,</span> specializing in acquiring, developing, and flipping underutilized properties. We also deliver end-to-end real estate solutions for international investors — locally and abroad.
              </p>
            </Reveal>
            <Reveal delay={1}>
              <div className="mt-10 flex items-center gap-6">
                <div className="h-px w-16 bg-[#D4AF37]" />
                <div className="text-sm text-[#8FA3B8] tracking-[0.2em] uppercase">U-NEXX Development Group Inc.</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OVERLAPPING ABOUT */}
      <section data-testid="home-about" className="py-20 md:py-32 px-6 md:px-12 lg:px-16 bg-[#0F1F35]">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative">
            <div className="zoom-wrap aspect-[4/5] lg:aspect-[5/6] w-full">
              <img src={IMAGES.luxury} alt="Interior" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-12 -right-6 lg:right-12 w-1/2 zoom-wrap aspect-[4/3] border-8 border-[#0F1F35]">
              <img src={IMAGES.exterior1} alt="Exterior" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-8 mt-16 lg:mt-0">
            <div className="overline mb-6">About the firm</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#F3EFE4] leading-[0.95] mb-8">
              A development partner —<br />not a brokerage.
            </h2>
            <p className="text-[#8FA3B8] leading-relaxed mb-6">
              U-NEXX Development Group Inc. is a full-service land and housing development and consulting company specializing in acquiring, developing, and flipping underutilized and abandoned properties.
            </p>
            <p className="text-[#8FA3B8] leading-relaxed mb-10">
              We also provide end-to-end real estate solutions for international clients — covering property sourcing, acquisition, development, renovation, and resale or rental management, both locally and abroad.
            </p>
            <Link to="/about" data-testid="home-about-link" className="btn-link">
              Read our story <span>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES — Bento grid */}
      <section data-testid="home-services" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-6">
              <div className="overline mb-6">What we do</div>
              <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-[#F3EFE4] leading-[0.9]">
                Four disciplines.<br />
                <span className="italic text-[#D4AF37]">One outcome.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end">
              <p className="text-[#8FA3B8] leading-relaxed">
                Whether you're flipping a single Edmonton bungalow or assembling land for a multi-unit build, our team manages every layer of the project so you only have to manage the outcome.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.no} delay={i}>
                <div className="relative border-r border-b border-white/10 p-8 md:p-10 min-h-[280px] flex flex-col justify-between hover:bg-[#12253D] transition-colors duration-500 group">
                  <div className="flex items-start justify-between">
                    <div className="font-serif-display text-2xl text-[#D4AF37]">{s.no}</div>
                    <ArrowRight size={18} className="text-[#8FA3B8] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition" />
                  </div>
                  <div>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[#F3EFE4] mb-3">{s.title}</h3>
                    <p className="text-sm text-[#8FA3B8] leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 flex justify-end">
            <Link to="/services" data-testid="home-services-link" className="btn-link">
              All services <span>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        data-testid="home-process"
        className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(11,12,16,0.88), rgba(11,12,16,0.92)), url(${IMAGES.texture})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <div className="overline mb-6">Our process</div>
            <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-[#F3EFE4]">
              From consultation to <span className="italic text-[#D4AF37]">completion</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-0 border-t border-white/10">
            {PROCESS.map((step, idx) => (
              <Reveal key={step.no} delay={idx}>
                <div className="md:border-r last:border-r-0 border-white/10 px-6 py-10 md:py-14 group hover:bg-white/[0.02] transition-colors">
                  <div className="font-serif-display text-5xl md:text-6xl text-[#D4AF37] mb-6">{step.no}</div>
                  <h3 className="font-serif-display text-xl text-[#F3EFE4] mb-3">{step.title}</h3>
                  <p className="text-xs text-[#8FA3B8] leading-relaxed">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="home-stats" className="py-20 md:py-28 px-6 md:px-12 lg:px-16 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-4 gap-y-12">
          {STATS.map((stat, i) => (
            <Reveal key={stat.label} delay={i} className="md:border-r last:md:border-r-0 border-white/10 md:px-8 first:md:pl-0">
              <div className="font-serif-display text-6xl md:text-7xl text-[#D4AF37] leading-none">{stat.number}</div>
              <div className="mt-4 text-xs tracking-[0.28em] uppercase text-[#8FA3B8]">{stat.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section data-testid="home-featured-projects" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="overline mb-6">Selected work</div>
              <h2 className="font-serif-display text-5xl md:text-6xl text-[#F3EFE4]">Recent projects.</h2>
            </div>
            <Link to="/projects" data-testid="home-all-projects" className="btn-link">
              View all <span>→</span>
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i}>
                <Link to="/projects" className="group block">
                  <div className="zoom-wrap aspect-[4/5] mb-5">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="overline mb-2 !text-[#8FA3B8] !text-[0.6rem]">{p.type} · {p.year}</div>
                      <h3 className="font-serif-display text-2xl text-[#F3EFE4] group-hover:text-[#D4AF37] transition">
                        {p.title}
                      </h3>
                      <div className="text-xs text-[#8FA3B8] mt-1">{p.location}</div>
                    </div>
                    <ArrowRight size={18} className="text-[#8FA3B8] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition shrink-0 mt-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="relative py-32 md:py-40 px-6 md:px-12 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.exterior2} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/85 to-[#0A1628]/60" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="overline mb-8">Ready when you are</div>
          <h2 className="font-serif-display text-5xl md:text-7xl lg:text-8xl text-[#F3EFE4] leading-[0.95] mb-10">
            Let's build something <span className="italic text-[#D4AF37]">that lasts.</span>
          </h2>
          <p className="text-[#8FA3B8] max-w-xl mx-auto mb-12 leading-relaxed">
            Partner with U-NEXX and let us steward your real estate investment journey from sourcing through delivery.
          </p>
          <button data-testid="home-cta-button" onClick={onOpenConsult} className="btn-primary">
            Schedule a Consultation <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>
    </div>
  );
}
